/**
 * Client-side image compression for uploads (KYC docs, deposit proofs, etc.).
 * Resizes to a max dimension + re-encodes as JPEG so phone photos drop from
 * several MB to a few hundred KB — faster uploads, and never hits body limits.
 * Non-images (PDFs) and already-small files are returned unchanged.
 */
export async function compressImage(
  file: File,
  opts?: { maxDim?: number; quality?: number; skipUnderBytes?: number },
): Promise<File> {
  if (!file.type.startsWith("image/")) return file; // leave PDFs etc. alone
  const maxDim = opts?.maxDim ?? 1600;
  const quality = opts?.quality ?? 0.8;
  const skipUnder = opts?.skipUnderBytes ?? 500 * 1024; // <500KB → already fine
  if (file.size <= skipUnder) return file;

  try {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as string);
      r.onerror = () => reject(new Error("read failed"));
      r.readAsDataURL(file);
    });
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error("decode failed"));
      i.src = dataUrl;
    });

    let { width, height } = img;
    if (Math.max(width, height) > maxDim) {
      const scale = maxDim / Math.max(width, height);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
    if (!blob || blob.size >= file.size) return file; // no gain → keep original
    const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file; // any failure → upload the original untouched
  }
}
